<template>
  <div class="section">
    <div class="section-title">
      {{ $t('Account Information') }}
    </div>
    <p class="divider small"></p>
    <div class="form-section">
      <div class="form-title">{{ $t('Account Details') }}</div>
      <div class="form-item inline">
        <p class="form-item-label">{{ $t('Account Types') }}</p>
        <p class="form-item-content account-type">{{ $t(userStore.userInfo.companyUser ? 'Enterprise Account' : 'Personal Account') }}</p>
      </div>
      <div class="form-item inline" v-if="!isSSO && !userStore.companyUser">
        <p class="form-item-label">{{ $t('UID') }}</p>
        <p class="form-item-content">{{ userStore.uid }}</p>
      </div>
      <div class="form-item inline" v-else-if="userStore.companyUser">
        <p class="form-item-label">{{ $t('Enterprise name') }}</p>
        <p class="form-item-content text-overflow">{{ userStore.userInfo?.companyName }}</p>
      </div>
    </div>
    <div class="form-section">
      <div class="form-title">{{ $t('Profile') }}</div>
      <div class="form-item inline">
        <p class="form-item-label">{{ $t('Profile Picture') }}</p>
        <div class="user-avatar">
          <Avatar
            :company-user="userStore.userInfo.companyUser"
            :src="userStore.userInfo.headerImg"
            :can-edit="!isSSO && !isCN"
            :nickname="userStore.nickName"
            :color="userStore.userInfo.color"
            :size="76"
            @on-ok="handleChangeAvatar"
          />
        </div>
      </div>
      <div class="form-item inline first-name">
        <p class="form-item-label">{{ $t(isCN ? 'Preferred name' : 'First name') }}</p>
        <el-input class="form-input" v-model="userData.firstName" :maxlength="nickNameMaxLen" :disabled="isSSO" />
      </div>
      <div class="form-item inline last-name" v-if="!isCN">
        <p class="form-item-label">{{ $t('Last name') }}</p>
        <el-input class="form-input" v-model="userData.lastName" :maxlength="nickNameMaxLen" :disabled="isSSO" />
      </div>
      <template class="form-item-group" v-if="!isSSO && !userStore.companyUser">
        <div class="form-item inline">
          <p class="form-item-label">{{ $t('Work') }}</p>
          <el-select class="form-input" v-model="userData.work" :placeholder="$t('What kind of work do you do?')">
            <el-option v-for="item in userWorkOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </div>
        <div class="form-item inline">
          <p class="form-item-label">{{ $t('Age') }}</p>
          <el-select class="form-input" v-model="userData.ageBetween" :placeholder="$t('What’s your age group?')">
            <el-option v-for="item in userAgeOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </div>
      </template>
      <div class="form-item inline" v-if="formHasChange">
        <p class="form-item-label"></p>
        <div class="form-item-group btn-group">
          <el-button class="btn" type="primary" :loading="saveBtnLoading" :disabled="saveBtnDisabled" @click="handleSaveForm">{{ $t('Save Changes') }}</el-button>
          <el-button class="btn" @click="handleResetForm">{{ $t('Cancel') }}</el-button>
        </div>
      </div>
    </div>

    <template v-if="!userStore.companyUser">
      <div class="section-title">
        {{ $t('Linked Account') }}
      </div>
      <p class="divider small"></p>
      <AccountManager></AccountManager>
    </template>
  </div>
</template>

<script setup>
import { marketingReport, setUserInfo } from '@/api/user/user'
import Avatar from '@/components/user/avatar.vue'
import gtagTools from '@/libs/gtag-tools'
import { userAgeOptions, userWorkOptions } from '@/libs/options'
import { postMessageToOpenerWindow } from '@/libs/utils'
import { appStore, userStore } from '@/stores'
import { ElMessage } from 'element-plus'
import { isEqual } from 'lodash'
import { computed, getCurrentInstance, nextTick, onMounted, reactive, ref, watch } from 'vue'
import AccountManager from '../components/account-manager.vue'
const { $t } = getCurrentInstance().proxy
const userData = reactive({})
const isCN = computed(() => appStore.isCN)
// 获取初始表单信息
const getDefaultUserData = () => {
  const defaultUserData = {}
  defaultUserData.firstName = userStore.userInfo.firstName
  defaultUserData.lastName = userStore.userInfo.lastName
  defaultUserData.work = userStore.userInfo.work
  defaultUserData.ageMax = userStore.userInfo.ageMax
  defaultUserData.ageMin = userStore.userInfo.ageMin
  if (userStore.userInfo.ageMax || userStore.userInfo.ageMin) {
    defaultUserData.ageBetween = `${userStore.userInfo.ageMin}_${userStore.userInfo.ageMax}`
  } else {
    defaultUserData.ageBetween = ''
  }
  return defaultUserData
}
// 重置表单数据
const resetDefaultUserData = () => {
  const defaultUserData = getDefaultUserData()
  Object.assign(userData, defaultUserData)
}

// 是否是sso登录
const isSSO = computed(() => userStore.userInfo.isSSO)
// 昵称最大长度
const nickNameMaxLen = 30
// 表单是否有修改，如果有则显示保存按钮
const formHasChange = ref(false)
const saveBtnDisabled = ref(false)
watch(
  () => userData,
  () => {
    const defaultUserData = getDefaultUserData()
    if (!isEqual(userData, defaultUserData)) {
      formHasChange.value = true
    } else {
      formHasChange.value = false
    }
    if ((appStore.isCN && !userData.firstName) || (!appStore.isCN && !userData.firstName && !userData.lastName)) {
      saveBtnDisabled.value = true
    } else {
      saveBtnDisabled.value = false
    }
  },
  { deep: true }
)
// 修改头像
const handleChangeAvatar = async data => {
  userStore.userInfo.headerImg = data.url
  const setUserData = {
    headerImg: data.filePath
  }
  try {
    await setUserInfo(setUserData)
    ElMessage.success($t('Saved success'))
    postMessageToOpenerWindow('userInfoUpdate')
    gtagTools.modifyProfile('photo')
  } catch (error) {
    console.log(error)
  }
}
// 取消表单修改
const handleResetForm = () => {
  resetDefaultUserData()
  nextTick(() => {
    formHasChange.value = false
  })
}
const saveBtnLoading = ref(false)
const handleSaveForm = async () => {
  if (saveBtnLoading.value) return
  saveBtnLoading.value = true
  // 用户数据
  const setUserData = {
    firstName: userData.firstName,
    lastName: isCN.value ? '' : userData.lastName,
  }
  // 营销数据
  const marketingData = {
    work: userData.work,
  }
  if (userData.ageBetween) {
    const ageBetweenArr = userData.ageBetween.split('_')
    marketingData.ageMin = parseInt(ageBetweenArr[0])
    marketingData.ageMax = parseInt(ageBetweenArr[1])
  }
  try {
    await setUserInfo(setUserData)
    await marketingReport(marketingData)
    Object.assign(userStore.userInfo, setUserData)
    Object.assign(userStore.userInfo, marketingData)
    ElMessage.success($t('Saved success'))
    postMessageToOpenerWindow('userInfoUpdate')
    formHasChange.value = false
    gtagTools.modifyProfile('firstname')
    gtagTools.modifyProfile('lastname')
  } catch (error) {
    ElMessage.error(error.message)
  }
  saveBtnLoading.value = false
}
onMounted(() => {
  resetDefaultUserData()
})
</script>

<style lang="scss" scoped>
.user-avatar {
  margin-right: auto;
}
.nickname-zh {
  justify-content: flex-start;
  .first-name {
    width: calc(90% + 16px) !important;
    max-width: calc(320px * 2 + 16px) !important;
  }
  .last-name {
    display: none;
  }
}
.form-item-group {
  margin-right: auto;
  flex: 1;
  &.btn-group {
    gap: 12px;
    .btn {
      min-width: 154px;
      margin: 0;
    }
  }
}
.form-title {
  margin-bottom: 24px;
}
.form-item {
  .account-type {
    background: #7479FF1A;
    color: var(--Color-1, #7479FF) !important;
    font-weight: 400;
    height: 32px;
    padding: 0 12px;
    border-radius: 8px;
    display: flex;
    align-items: center;
  }
  .form-item-content {
    text-align: right;
  }
}

@media screen and (max-width: 430px) {
  .form-item-group {
    .btn {
      zoom: 0.8;
      min-width: unset;
    }
  }
}
</style>
